import classNames from "classnames";
import { Upload } from "neetoicons";
import { Button, Typography, Pane } from "neetoui";
import { useDropzone } from "react-dropzone";
import { Trans, useTranslation } from "react-i18next";

import { ONE_KILOBYTE_IN_BYTES } from "./constants";
import Requirements from "./Requirements";

const BulkUploadPane = ({ isOpen, fileSize, onClose, handleFileSubmit }) => {
  const { getRootProps, getInputProps, isDragActive, fileRejections } =
    useDropzone({
      accept: { "text/csv": [] },
      maxFiles: 1,
      maxSize: fileSize * ONE_KILOBYTE_IN_BYTES,
      onDrop: files => files[0] && handleFileSubmit(files[0]),
    });

  const errors = fileRejections[0]?.errors.map(({ message, code }) => (
    <Typography
      className="neeto-ui-text-error-500 items"
      data-testid="file-input-error-message"
      key={code}
      style="body3"
    >
      {message}
    </Typography>
  ));

  const { t } = useTranslation();

  return (
    <Pane {...{ isOpen, onClose }} data-testid="bulk-upload-pane">
      <div tabIndex={0}>
        <Pane.Header>
          <Typography
            data-testid="bulk-upload-header"
            style="h2"
            weight="semibold"
          >
            {t("neetoMolecules.shareViaLink.bulkUpload")}
          </Typography>
        </Pane.Header>
        <Pane.Body>
          <div className="flex w-full flex-col gap-y-4">
            <Requirements {...{ fileSize }} />
            <div
              {...getRootProps({
                className: classNames(
                  "flex flex-col p-2 border neeto-ui-border-primary-500 border-dashed neeto-ui-rounded-md transition duration-500 ease-in-out hover:neeto-ui-bg-gray-100 cursor-pointer items-center",
                  {
                    "neeto-ui-border-gray-100": isDragActive,
                    "neeto-ui-border-error neeto-ui-bg-pastel-red hover:neeto-ui-bg-pastel-red":
                      fileRejections.length,
                  }
                ),
              })}
            >
              <div className="flex flex-col items-center gap-y-3 p-4">
                <Upload className="neeto-ui-text-primary-500" size={32} />
                <input {...getInputProps()} data-testid="csv-upload-input" />
                {isDragActive ? (
                  <Typography style="body2">
                    {t(
                      "neetoMolecules.shareViaLink.bulkUploadPane.dropFileHere"
                    )}
                  </Typography>
                ) : (
                  <div className="flex flex-col items-center gap-y-1">
                    <div className="flex w-full items-center justify-center gap-x-1">
                      <Trans
                        i18nKey="neetoMolecules.shareViaLink.bulkUploadPane.dragNDrop"
                        components={{
                          Button: <Button style="link" />,
                        }}
                      />
                    </div>
                    <Typography
                      className="neeto-ui-text-gray-700"
                      style="body3"
                    >
                      <Trans
                        i18nKey="neetoMolecules.shareViaLink.bulkUploadPane.supportedFormat"
                        values={{ fileType: "csv" }}
                        components={{
                          code: (
                            <code className="neeto-ui-typography neeto-ui-text-body3 neeto-ui-bg-white m-0 border-s-0 p-0" />
                          ),
                        }}
                      />{" "}
                      {fileSize &&
                        t(
                          "neetoMolecules.shareViaLink.bulkUploadPane.supportedSize",
                          { fileSize }
                        )}
                    </Typography>
                  </div>
                )}
              </div>
              {errors}
            </div>
          </div>
        </Pane.Body>
        <Pane.Footer className="flex items-center gap-x-2">
          <Button
            data-testid="bulk-upload-cancel-button"
            label={t("neetoMolecules.common.actions.cancel")}
            style="text"
            type="reset"
            onClick={onClose}
          />
        </Pane.Footer>
      </div>
    </Pane>
  );
};

export default BulkUploadPane;
